import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

const initialState = {
  complaints: [],
  loading: false,
  error: null,
  currentComplaint: null,
};

export const fetchComplaints = createAsyncThunk(
  'complaint/fetchComplaints',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (filters.areaId) params.areaId = filters.areaId;
      if (filters.status) params.status = filters.status;
      if (filters.userId) params.userId = filters.userId;

      const response = await api.get('/complaints', { params });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchComplaintById = createAsyncThunk(
  'complaint/fetchComplaintById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/complaints/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createComplaint = createAsyncThunk(
  'complaint/createComplaint',
  async (complaintData, { rejectWithValue }) => {
    try {
      const response = await api.post('/complaints', complaintData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateComplaintStatus = createAsyncThunk(
  'complaint/updateComplaintStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/complaints/${id}/status`, { status });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const complaintSlice = createSlice({
  name: 'complaint',
  initialState,
  reducers: {
    clearCurrentComplaint(state) {
      state.currentComplaint = null;
    },
    setComplaintStatus(state, action) {
      const { id, status } = action.payload;
      const complaint = state.complaints.find(c => c.id === id);
      if (complaint) {
        complaint.status = status;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchComplaints.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchComplaints.fulfilled, (state, action) => {
        state.loading = false;
        state.complaints = action.payload;
      })
      .addCase(fetchComplaints.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchComplaintById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchComplaintById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentComplaint = action.payload;
      })
      .addCase(fetchComplaintById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createComplaint.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createComplaint.fulfilled, (state, action) => {
        state.loading = false;
        state.complaints.unshift(action.payload);
      })
      .addCase(createComplaint.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateComplaintStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateComplaintStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.complaints.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.complaints[index] = action.payload;
        }
        if (state.currentComplaint?.id === action.payload.id) {
          state.currentComplaint = action.payload;
        }
      })
      .addCase(updateComplaintStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentComplaint, setComplaintStatus } = complaintSlice.actions;

export default complaintSlice.reducer;
